/**
 * ItemComments.jsx — flat comments + rating on a single library item.
 * Drop into any item detail view (reader page, series modal etc).
 *
 * Props:
 *   refId  — the unique identifier of the item (book_id or series_id)
 *
 * Backed by:
 *   /api/items/{refId}/comments   (GET/POST)
 *   /api/comments/{comment_id}    (DELETE — own/admin)
 *   /api/ratings/{ref_type}/{ref_id} (POST — existing rating endpoint)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Send, Loader2, Trash2, Star } from 'lucide-react';
import { FlashReadButton } from './FlashReadOverlay';
import { toast } from 'sonner';
import { authFetch, checkAuth } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

const StarRating = ({ refId, refType = 'book' }) => {
  const [value, setValue] = useState(0);
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const rate = async (stars) => {
    if (submitting) return;
    setSubmitting(true);
    setValue(stars);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/ratings/${refType}/${refId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stars }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `Rating failed (${r.status})`);
      }
      toast.success(`Rated ${stars} star${stars === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err.message || 'Could not rate');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="flex items-center gap-1" data-testid="item-rating-stars">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => rate(s)}
          onMouseEnter={() => setHover(s)}
          onMouseLeave={() => setHover(0)}
          disabled={submitting}
          className="p-0.5"
          aria-label={`Rate ${s} stars`}
          data-testid={`item-rating-star-${s}`}
        >
          <Star
            className={`w-5 h-5 ${
              (hover || value) >= s ? 'fill-amber-400 text-amber-400' : 'text-slate-500'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const ItemComments = ({ refId, refType = 'book' }) => {
  const [me, setMe] = useState(null);
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => { setMe(await checkAuth()); })(); }, []);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/items/${encodeURIComponent(refId)}/comments`);
      if (r.ok) {
        const d = await r.json();
        setComments(d.comments || []);
      }
    } finally { setLoading(false); }
  }, [refId]);

  useEffect(() => { if (refId) fetchComments(); }, [refId, fetchComments]);

  const post = async () => {
    const text = draft.trim();
    if (!text) return;
    setSubmitting(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/items/${encodeURIComponent(refId)}/comments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: text }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Failed (${r.status})`);
      setComments((prev) => [d.comment, ...prev]);
      setDraft('');
    } catch (err) {
      toast.error(err.message || 'Could not post');
    } finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!confirm('Delete this comment?')) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/api/comments/${id}`, { method: 'DELETE' });
      if (r.ok) setComments((prev) => prev.filter((c) => c.comment_id !== id));
    } catch (_) { toast.error('Could not delete'); }
  };

  return (
    <section className="rounded-xl bg-slate-800/50 border border-slate-700 p-4 space-y-4" data-testid="item-comments">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-300">Rate & Discuss</h3>
        <StarRating refId={refId} refType={refType} />
      </div>

      <div className="flex items-start gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 2000))}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); post(); } }}
          rows={2}
          placeholder="Share your thoughts…"
          className="flex-1 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-none"
          data-testid="item-comment-input"
        />
        <button
          type="button"
          onClick={post}
          disabled={submitting || !draft.trim()}
          className="p-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-600 text-white"
          data-testid="item-comment-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>

      <div className="space-y-3" data-testid="item-comment-list">
        {loading && <div className="text-center text-slate-400 text-sm py-2"><Loader2 className="w-3.5 h-3.5 inline-block animate-spin mr-1" /> Loading…</div>}
        {!loading && comments.length === 0 && <div className="text-xs text-slate-500 italic">No comments yet — be the first.</div>}
        {comments.map((c) => {
          const mine = c.user_id === me?.user_id;
          return (
            <article key={c.comment_id} className="flex items-start gap-2.5" data-testid={`item-comment-${c.comment_id}`}>
              {c.author?.picture ? (
                <img src={c.author.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-white truncate">{c.author?.name || 'Anonymous'}</span>
                  {c.author?.role === 'admin' && (
                    <span className="text-[9px] uppercase font-bold text-red-300 bg-red-600/20 border border-red-500/30 rounded-full px-1.5 py-0.5">ADMIN</span>
                  )}
                  {c.author?.role === 'writer' && (
                    <span className="text-[9px] uppercase font-bold text-purple-300 bg-purple-600/20 border border-purple-500/30 rounded-full px-1.5 py-0.5">WRITER</span>
                  )}
                  <span className="text-[10px] text-slate-500 ml-auto">{formatTime(c.created_at)}</span>
                </div>
                <p className="text-sm text-slate-200 whitespace-pre-wrap break-words mt-0.5">{c.body}</p>
              </div>
              {(mine || me?.role === 'admin') && (
                <button type="button" onClick={() => remove(c.comment_id)} className="p-1 rounded hover:bg-slate-700" aria-label="Delete comment">
                  <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                </button>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default ItemComments;
